import { useState } from 'react';
import { Trash2, Upload } from 'lucide-react';
import { useUploadEvidenceUrl } from '@/api/practicalObservations';
import type { EvidenceDraft, ObservationEvidenceType } from '@/types';

function detectEvidenceType(contentType: string): ObservationEvidenceType {
  if (contentType.startsWith('image/')) return 'image';
  if (contentType.startsWith('video/')) return 'video';
  if (contentType.startsWith('application/')) return 'document';
  return 'other';
}

type Props = {
  requestId: number;
  value: EvidenceDraft[];
  onChange: (next: EvidenceDraft[]) => void;
};

export function EvidenceDropzone({ requestId, value, onChange }: Props) {
  const uploadUrl = useUploadEvidenceUrl();
  const [error, setError] = useState<string | null>(null);

  async function handleFiles(files: FileList) {
    setError(null);
    try {
      const newItems: EvidenceDraft[] = [];
      for (const file of Array.from(files)) {
        const presigned = await uploadUrl.mutateAsync({
          requestId,
          file_name: file.name,
          content_type: file.type,
        });
        const res = await fetch(presigned.data.upload_url, {
          method: 'PUT',
          body: file,
          headers: { 'Content-Type': file.type },
        });
        if (!res.ok) {
          throw new Error(`No se pudo subir "${file.name}": HTTP ${res.status}`);
        }
        newItems.push({
          file_key: presigned.data.key,
          file_name: file.name,
          file_size: file.size,
          content_type: file.type,
          evidence_type: detectEvidenceType(file.type),
        });
      }
      onChange([...value, ...newItems]);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error al subir archivos');
    }
  }

  return (
    <div className="space-y-2">
      <label className="flex cursor-pointer items-center gap-2 rounded border border-dashed border-border px-3 py-2 text-sm text-muted-foreground hover:bg-accent/30">
        <Upload className="h-4 w-4" />
        <span>Adjuntar archivos (imagen, video o documento)</span>
        <input
          type="file"
          multiple
          className="hidden"
          disabled={uploadUrl.isPending}
          onChange={(e) => {
            if (e.target.files) {
              void handleFiles(e.target.files);
              e.target.value = '';
            }
          }}
        />
      </label>
      {uploadUrl.isPending && <p className="text-xs text-primary">Subiendo…</p>}
      {error && <p className="text-xs text-rose-600">{error}</p>}
      {value.length > 0 && (
        <ul className="space-y-1 text-sm">
          {value.map((item, idx) => (
            <li
              key={item.file_key}
              className="flex items-center justify-between rounded border border-border bg-card px-3 py-1.5"
            >
              <span className="truncate">
                <span className="font-medium">{item.file_name}</span>
                <span className="ml-2 text-xs text-muted-foreground">
                  {(item.file_size / 1024).toFixed(1)} KB · {item.evidence_type}
                </span>
              </span>
              <button
                type="button"
                onClick={() => onChange(value.filter((_, i) => i !== idx))}
                className="rounded p-1 text-rose-600 hover:bg-rose-50"
                aria-label="Quitar"
              >
                <Trash2 className="h-4 w-4" />
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
